import { useState, Fragment } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TangigLockup } from "@/components/brand/TangigLogo";
import { API } from "@/context/AuthContext";

/* ============================================================
   TANGIG — Footer global (identique sur toutes les pages)
   Fond gris clair (#F5F5F7). Monté une seule fois dans PageLayout.
   Tokens TANGIG résolus en dur (les variables CSS ne sont pas
   chargées globalement dans ce projet Vite).
   TODO : finaliser les href "#" des réseaux sociaux.
   ============================================================ */

/* ---- tokens TANGIG (valeurs en dur) ---- */
const C = {
  ink:       "#0A0A0A",
  ink2:      "#3A3A3C",
  grey1:     "#4A4A4E",   // gris de texte assombri (ex #6E6E73) pour plus de lisibilité
  grey2:     "#6E6E73",   // ex #86868B
  line:      "#E5E5E7",
  line2:     "rgba(0,0,0,0.08)",
  surface:   "#F5F5F7",
  surface2:  "#ffffff",
  blue:      "#1D5FD1",
  blueHover: "#17509C",
  green:     "#22A45D",
  ring:      "0 0 0 3px rgba(29,95,209,0.15)",
  font:      "Inter, sans-serif",
};

// Règle : aucun lien mort dans le footer. Un lien vers "#" ou vers une page qui
// n'existe pas coûte plus en crédibilité qu'il ne rapporte — on le remet le jour
// où la page existe (blog, "Comment ça marche", programme d'affiliation public).
const COLS = [
  {
    title: "Plateforme",
    links: [
      { label: "Explorer les systèmes", to: "/marketplace" },
      { label: "Vision", to: "/vision" },
      { label: "Devenir créateur", to: "/devenir-createur" },
    ],
  },
  {
    title: "Créateurs",
    links: [
      // Le formulaire de candidature vit en bas de /devenir-createur (#candidature),
      // plus sur la page Fondateurs (absorbée par /vision).
      { label: "Programme fondateurs", to: "/devenir-createur#candidature" },
      // Redirige vers /connexion si personne n'est connecté (SellerDashboard).
      { label: "Espace créateur", to: "/vendeur/dashboard" },
    ],
  },
  {
    title: "Ressources",
    links: [
      { label: "Le Standard TANGIG", to: "/le-standard" },
      { label: "FAQ", to: "/faq" },
      { label: "Nous contacter", href: "mailto:contact@tangig.com" },
    ],
  },
];

const LEGAL = [
  // Les CGV manquaient : elles régissent la vente, elles doivent être
  // accessibles depuis toutes les pages.
  { label: "CGV", to: "/legal/cgv" },
  { label: "CGU", to: "/legal/cgu" },
  { label: "Confidentialité", to: "/legal/confidentialite" },
  { label: "Mentions légales", to: "/legal/mentions" },
  // "Cookies" pointait sur "#" : le sujet est traité dans Confidentialité (§7).
];

// N'afficher que ce qui sera réellement encaissable : le paiement passe par
// Stripe Checkout (carte). Amex et Apple Pay sont des options à activer dans le
// dashboard Stripe (Apple Pay demande en plus une vérification de domaine) → à
// remettre ici une fois activées.
const PAYMENTS = ["VISA", "Mastercard", "CB", "Stripe"];

const RESPONSIVE_CSS = `
  @media (max-width: 900px) {
    .sf-grid { grid-template-columns: 1fr 1fr !important; }
    .sf-brand { grid-column: 1 / -1 !important; }
  }
  @media (max-width: 560px) {
    .sf-grid { grid-template-columns: 1fr !important; }
    .sf-form { flex-direction: column !important; }
    /* Bouton minimaliste : largeur au contenu (pas pleine largeur), aligné
       à gauche, et hauteur correcte (padding vertical) pour ne pas être écrasé. */
    .sf-btn {
      width: auto !important;
      align-self: flex-start !important;
      padding: 14px 30px !important;
    }
  }
`;

/* Rend un lien interne (react-router) ou externe/mailto/ancre. */
function FooterLink({ to, href, style, children }) {
  const props = {
    style,
    onMouseEnter: hoverIn,
    onMouseLeave: hoverOut,
  };
  return to ? (
    <Link to={to} {...props}>{children}</Link>
  ) : (
    <a href={href} {...props}>{children}</a>
  );
}

/* Trois publics, trois usages du site. Sans ce tri, la liste ne dit ni quoi
   ecrire ni a qui : la question posee juste apres l'inscription vaut plus
   que l'adresse elle-meme. */
const INTENTS = [
  { value: "buy",    label: "Je veux acheter des systèmes"          },
  { value: "build",  label: "Je veux construire des systèmes"       },
  { value: "follow", label: "Je veux simplement suivre le mouvement" },
];

export default function SiteFooter() {
  const [email, setEmail] = useState("");
  const [focus, setFocus] = useState(false);
  /* "email" → "intent" → "done". L'adresse part des le premier temps : celui
     qui ne repond pas a la question reste inscrit malgre tout. */
  const [step, setStep] = useState("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/subscribers`, { email: email.trim() });
      setStep("intent");
    } catch (err) {
      /* Ne pas accuser l'adresse quand c'est le serveur qui n'a pas repondu :
         seul un 422 dit vraiment que l'adresse est invalide. */
      const status = err?.response?.status;
      setError(
        status === 429 ? "Trop de tentatives. Réessayez dans un moment."
        : status === 422 ? "Cette adresse e-mail semble invalide."
        : "L'inscription n'a pas pu aboutir. Réessayez dans un moment."
      );
    } finally {
      setBusy(false);
    }
  };

  const chooseIntent = async (value) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/subscribers`, { email: email.trim(), intent: value });
    } catch {
      /* L'adresse est deja enregistree : echouer sur la precision ne doit pas
         renvoyer le visiteur au formulaire. */
    } finally {
      setBusy(false);
      setStep("done");
    }
  };

  return (
    <footer style={S.footer} data-navbar-theme="light">
      <style>{RESPONSIVE_CSS}</style>
      <div style={S.inner}>
        {/* ===== Newsletter ===== */}
        <div style={S.newsletter}>
          <div style={{ flex: "1 1 380px", minWidth: 300 }}>
            <h3 style={S.h3}>Rejoignez le mouvement.</h3>
            <p style={S.lede}>
              Les systèmes que les entreprises demandent. Les experts qui les construisent.
              Les prochains à émerger.
            </p>
          </div>

          {/* Le message d'erreur vit SOUS la ligne, jamais dedans : la ligne est
              en align-items:stretch, un enfant plus haut y etirerait le bouton. */}
          {step === "email" && (
            <div style={S.formWrap}>
              <form onSubmit={onSubmit} className="sf-form" style={S.formRow}>
                <div style={{ ...S.field, borderColor: focus ? C.blue : C.line, boxShadow: focus ? C.ring : "none" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.grey2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="3" />
                    <path d="m3 6 9 6 9-6" />
                  </svg>
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    style={S.input}
                  />
                </div>
                <button
                  type="submit"
                  className="sf-btn"
                  style={S.btn}
                  disabled={busy}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.ink; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.ink; }}
                >
                  {busy ? "Un instant…" : "Rejoindre TANGIG"}
                </button>
              </form>
              {error && <p style={S.error}>{error}</p>}
            </div>
          )}

          {/* La question ne bloque rien : l'adresse est deja enregistree. */}
          {step === "intent" && (
            <div style={S.formWrap}>
              <p style={S.intentLabel}>Je suis :</p>
              <div style={S.intentRow}>
                {INTENTS.map((i) => (
                  <button
                    key={i.value}
                    type="button"
                    style={S.intentChip}
                    disabled={busy}
                    onClick={() => chooseIntent(i.value)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = C.surface)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = C.surface2)}
                  >
                    {i.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === "done" && (
            <div style={S.formWrap}>
              <p style={{ ...S.lede, maxWidth: "none" }}>
                <b style={{ color: C.ink }}>C'est noté.</b> Vous êtes inscrit — nous vous écrivons
                quand il y a quelque chose à montrer, pas avant.
              </p>
            </div>
          )}
        </div>

        {/* ===== Colonnes de liens ===== */}
        <div className="sf-grid" style={S.grid}>
          {/* Marque */}
          <div className="sf-brand" style={{ minWidth: 200 }}>
            <TangigLockup size={22} color={C.ink} style={S.wordmark} />
            <p style={S.tagline}>Vendez un résultat. Pas un fichier.</p>
            <div style={{ display: "flex", gap: 12 }}>
              <Social label="X">
                <path d="M18.9 2H22l-7.3 8.4L23.3 22h-6.8l-5.3-6.9L5.1 22H2l7.8-9L1 2h6.9l4.8 6.3zm-1.1 18h1.7L7.3 3.8H5.5z" />
              </Social>
              <Social label="LinkedIn">
                <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05C21.4 8.65 22 11 22 14.1V21h-4v-6.1c0-1.45-.03-3.3-2-3.3-2.01 0-2.32 1.57-2.32 3.2V21h-4z" />
              </Social>
              <Social label="Instagram" stroke>
                <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
                <circle cx="12" cy="12" r="4.2" />
                <circle cx="17.6" cy="6.4" r="1.1" fill="currentColor" stroke="none" />
              </Social>
              <Social label="YouTube">
                <path d="M23 12s0-3.4-.43-5.03a2.6 2.6 0 0 0-1.83-1.84C18.9 4.7 12 4.7 12 4.7s-6.9 0-8.74.43A2.6 2.6 0 0 0 1.43 6.97C1 8.6 1 12 1 12s0 3.4.43 5.03c.24.9.94 1.6 1.83 1.84C5.1 19.3 12 19.3 12 19.3s6.9 0 8.74-.43a2.6 2.6 0 0 0 1.83-1.84C23 15.4 23 12 23 12zM9.8 15.3V8.7l5.7 3.3z" />
              </Social>
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <div style={S.colTitle}>{col.title}</div>
              <ul style={S.list}>
                {col.links.map((l) => (
                  <li key={l.label}>
                    <FooterLink to={l.to} href={l.href} style={S.link}>
                      {l.label}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ===== Paiement sécurisé ===== */}
        <div style={S.payRow}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: C.grey1 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2 4 5v6c0 5 3.4 8.6 8 11 4.6-2.4 8-6 8-11V5z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            Paiement sécurisé.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {PAYMENTS.map((p) => (
              <span key={p} style={S.payBadge}>{p}</span>
            ))}
          </div>
        </div>

        {/* ===== Barre légale ===== */}
        <div style={S.legalBar}>
          <div style={S.legalLinks}>
            <span>Copyright © {new Date().getFullYear()} TANGIG. Tous droits réservés.</span>
            <span style={S.legalGroup}>
              {LEGAL.map((l, i) => (
                <Fragment key={l.label}>
                  {i > 0 && <span aria-hidden="true" style={S.legalSep} />}
                  {l.to ? (
                    <Link to={l.to} style={{ color: C.grey2, textDecoration: "none" }}>
                      {l.label}
                    </Link>
                  ) : (
                    <a href={l.href} style={{ color: C.grey2, textDecoration: "none" }}>
                      {l.label}
                    </a>
                  )}
                </Fragment>
              ))}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.grey2 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3c2.5 2.7 3.8 6 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-6-3.8-9S9.5 5.7 12 3z" />
            </svg>
            France (EUR €)
          </div>
        </div>
      </div>
    </footer>
  );
}

function Social({ label, href = "#", children, stroke }) {
  // TODO: passer les vraies URLs des réseaux (href="...") sur chaque <Social />
  return (
    <a
      href={href}
      aria-label={label}
      style={S.social}
      onMouseEnter={(e) => (e.currentTarget.style.color = C.blue)}
      onMouseLeave={(e) => (e.currentTarget.style.color = C.ink)}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill={stroke ? "none" : "currentColor"}
        stroke={stroke ? "currentColor" : undefined}
        strokeWidth={stroke ? 2 : undefined}
      >
        {children}
      </svg>
    </a>
  );
}

const hoverIn = (e) => (e.currentTarget.style.color = C.ink);
const hoverOut = (e) => (e.currentTarget.style.color = C.grey1);

/* ---- styles (tokens TANGIG en dur) ---- */
const S = {
  footer: { background: "#FAFAFA", fontFamily: C.font, color: C.ink, padding: "170px 24px 40px", display: "flex", justifyContent: "center", boxSizing: "border-box" },
  inner: { width: "100%", maxWidth: 1200 },
  newsletter: { display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 40, paddingBottom: 52, borderBottom: `1px solid ${C.line2}` },
  h3: { fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15, margin: "0 0 12px" },
  lede: { fontSize: 16, lineHeight: 1.5, color: C.grey1, margin: 0, maxWidth: 440 },
  // La colonne de droite porte la largeur ; la ligne champ + bouton reste un
  // flex a deux enfants, pour que le bouton s'aligne exactement sur le champ.
  formWrap: { flex: "1 1 380px", minWidth: 300 },
  formRow: { display: "flex", gap: 12, alignItems: "stretch" },
  field: { flex: 1, display: "flex", alignItems: "center", gap: 10, background: C.surface2, border: `1px solid ${C.line}`, borderRadius: 12, padding: "0 16px", transition: "border-color 0.2s ease, box-shadow 0.2s ease" },
  input: { flex: 1, border: "none", outline: "none", background: "transparent", fontFamily: C.font, fontSize: 16, color: C.ink, padding: "13px 0", letterSpacing: "-0.01em", minWidth: 0 },
  btn: { border: `1px solid ${C.ink}`, cursor: "pointer", background: "transparent", color: C.ink, fontFamily: C.font, fontSize: 15, fontWeight: 500, padding: "0 22px", borderRadius: 8, whiteSpace: "nowrap", transition: "background 0.2s ease, color 0.2s ease" },
  error: { margin: "8px 0 0", fontSize: 13, color: "#C0392B" },
  intentLabel: { margin: "0 0 10px", fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", color: C.ink },
  intentRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  intentChip: { border: `1px solid ${C.line}`, background: C.surface2, color: C.ink, fontFamily: C.font, fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em", padding: "10px 16px", borderRadius: 999, cursor: "pointer", textAlign: "left", transition: "background 0.2s ease" },
  grid: { display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 40, padding: "56px 0" },
  // Le lockup porte sa propre typographie (Albert Sans 600, -0.03em) : ne rien
  // imposer ici au-delà de l'espacement, sinon le logo se déforme.
  wordmark: { marginBottom: 14 },
  tagline: { fontSize: 15, lineHeight: 1.5, color: C.grey1, margin: "0 0 24px", maxWidth: 240 },
  social: { width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", color: C.ink, background: "transparent", transition: "color 0.2s ease" },
  colTitle: { fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", textTransform: "none", color: C.ink, marginBottom: 18 },
  list: { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 13, fontSize: 15 },
  link: { color: C.grey1, textDecoration: "none", transition: "color 0.2s ease" },
  payRow: { display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "26px 0", borderBottom: `1px solid ${C.line2}` },
  payBadge: { display: "flex", alignItems: "center", justifyContent: "center", height: 26, padding: "0 10px", border: `1px solid ${C.line}`, borderRadius: 5, fontSize: 12, fontWeight: 700, letterSpacing: "0.02em", color: C.ink2 },
  legalBar: { display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16, paddingTop: 22 },
  legalLinks: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 20px", fontSize: 13, color: C.grey2 },
  // Les 3 liens légaux restent groupés (nowrap) : un séparateur ne doit jamais
  // se retrouver seul en fin de ligne quand la barre passe sur 2 lignes.
  legalGroup: { display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 14 },
  legalSep: { width: 1, height: 10, flex: "0 0 auto", background: "rgba(0,0,0,0.18)", borderRadius: 1 },
};
